/**
 * eventStarters — starter-node resolution for EventRuntime.fireEvent.
 *
 * Split out of EventRuntime.js so it can be unit-tested: EventRuntime transitively
 * imports the asset/API layer (extensionless specifiers only Vite resolves), so it
 * cannot be imported under this repo's bare `node --test`. Same reasoning as
 * cameraController's THREE-free module-scope helpers.
 *
 * Two jobs:
 *
 * 1. CACHE the per-type starter list. fireEvent used to run
 *    `nodes.filter(n => n.type === type)` on every fire. Fine for network/keypress
 *    events; not fine for input events (onPointerDrag/onWheel fire once per FRAME
 *    while dragging), which puts that allocation on the render hot path — CLAUDE.md
 *    forbids per-frame allocation there.
 *
 * 2. MATCH bound nodes. fireEvent runs every node of a type, but some input nodes
 *    carry a binding: an `onKeyDown` bound to "w" must not fire for "a"; an
 *    `onPointerDrag` filtered to the middle button must not fire for a left-drag.
 *    These predicates are what make bound input nodes possible at all — previously
 *    only `inputKey` had this, hand-rolled in its own parallel path (fireInputKey).
 */

import { normalizeKey } from './inputComponent.js'

// Cache invalidation is keyed on the nodes ARRAY IDENTITY, deliberately, and NOT on
// subscribeEventGraph(): EventGraph.jsx:640 reassigns eventGraphState.nodes WITHOUT
// calling notifyEventGraphChange(), so a notification-driven cache would go stale on
// the single most common mutation path — editing the graph in the editor.
//
// REQUIRED INVARIANT: every mutator of eventGraphState.nodes must REASSIGN the array,
// never push/splice in place. That holds today — EventGraph.jsx (React state),
// BlueprintEditor.jsx:1687/1826 and loadBlueprint.js:97/157 all reassign, and nothing
// mutates the array in place. Identity keying is more reliable than the existing
// notification for reassigning mutators, but it is NOT self-healing in general: an
// in-place mutator is exactly the case it cannot catch, and its added node would stay
// invisible until some unrelated edit replaced the array.
let _ref = null
const _cache = new Map()   // type → node[]

/** Cached `nodes.filter(n => n.type === type)`. Allocates only after a graph edit. */
export function startersFor(type, nodes) {
  if (nodes !== _ref) { _cache.clear(); _ref = nodes }
  let list = _cache.get(type)
  if (!list) { list = nodes.filter(n => n.type === type); _cache.set(type, list) }
  return list
}

/** Force a rebuild on the next call. Test/HMR seam — not needed in normal operation. */
export function invalidateStarterCache() { _ref = null; _cache.clear() }

/**
 * A key-bound node matches when its bound key equals the payload key. Both sides go
 * through normalizeKey, which also fixes a pre-existing bug: InputKeyNode's capture
 * widget STORES ' ' as 'Space', but App.jsx fired the raw `e.key` (' '), and the old
 * comparison was `'space' === ' '` — so a spacebar binding never matched.
 * An unbound node (no key set) matches nothing.
 */
export function keyMatches(node, data) {
  const bound = normalizeKey(node?.data?.key ?? '')
  return bound !== '' && bound === normalizeKey(data?.key ?? '')
}

/** A pointer node matches its configured button, or any button when unset/'any'. */
export function buttonMatches(node, data) {
  const want = node?.data?.button ?? 'any'
  return want === 'any' || want === data?.button
}

const STARTER_FILTERS = {
  inputKey:      keyMatches,
  onKeyDown:     keyMatches,
  onKeyUp:       keyMatches,
  onPointerDown: buttonMatches,
  onPointerUp:   buttonMatches,
  onPointerDrag: buttonMatches,
}

/**
 * Should this starter node run for this payload? True for every event type without a
 * binding concept. Predicates read LIVE node.data, so editing a binding in place takes
 * effect immediately — the cache above only caches the by-type list, never a verdict.
 */
export function starterMatches(type, node, data) {
  const f = STARTER_FILTERS[type]
  return f ? f(node, data) : true
}

/** True when `type` filters its starters by a node binding. */
export function hasStarterFilter(type) {
  return Object.prototype.hasOwnProperty.call(STARTER_FILTERS, type)
}
